import { useEffect, useMemo, useState } from "react";
import { collection, doc, limit, onSnapshot, query, where } from "firebase/firestore";
import { db } from "@/services/firebase";
import { useAuth } from "@/contexts/AuthContext";
import type { DeviceEnrolment, Group, Kit, Scenario, User } from "@/types";
import { collator as collatorFor, type Locale } from "@/i18n";

/**
 * Everyone in the institution, live.
 *
 * Read straight from Firestore rather than through a callable. The rules
 * already say a staff member may read the users of their own institution, and a
 * subscription means a password reset or a disable made in another tab is
 * visible here without anybody pressing anything.
 */
export function usePeople(): { people: User[]; loading: boolean } {
  const { user } = useAuth();
  const institutionId = user?.institutionId ?? undefined;
  const [people, setPeople] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!institutionId) {
      setPeople([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    return onSnapshot(
      query(collection(db(), "users"), where("institutionId", "==", institutionId)),
      (snapshot) => {
        setPeople(snapshot.docs.map((document) => document.data() as User));
        setLoading(false);
      },
      () => {
        setPeople([]);
        setLoading(false);
      }
    );
  }, [institutionId]);

  return { people, loading };
}

/** Which groups each person is in, by uid. */
export function groupsByUid(groups: readonly Group[]): Map<string, Group[]> {
  const index = new Map<string, Group[]>();

  const add = (uid: string, group: Group) => {
    const existing = index.get(uid);
    if (existing) existing.push(group);
    else index.set(uid, [group]);
  };

  for (const group of groups) {
    for (const uid of group.teacherUids) add(uid, group);
    for (const entry of group.roster) add(entry.uid, group);
  }
  return index;
}

/** Sorted the way a register is read: by name, in the reader's collation. */
export function sortByName(people: readonly User[], locale: Locale): User[] {
  const collator = collatorFor(locale);
  return [...people].sort((a, b) => collator.compare(a.displayName, b.displayName));
}

export function useGroups(): { groups: Group[]; loading: boolean } {
  const { user } = useAuth();
  const institutionId = user?.institutionId ?? undefined;
  const [groups, setGroups] = useState<Group[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!institutionId) {
      setGroups([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    return onSnapshot(
      collection(db(), "institutions", institutionId, "groups"),
      (snapshot) => {
        setGroups(snapshot.docs.map((document) => document.data() as Group));
        setLoading(false);
      },
      () => {
        setGroups([]);
        setLoading(false);
      }
    );
  }, [institutionId]);

  return { groups, loading };
}

/** Group names that already exist, lower-cased, for the import review. */
export function useExistingGroupNames(): ReadonlySet<string> {
  const { groups } = useGroups();
  return useMemo(
    () => new Set(groups.map((group) => group.name.trim().toLowerCase())),
    [groups]
  );
}

/**
 * How many devices each person has enrolled, live.
 *
 * One subscription per group rather than a collection-group query: the rules
 * bind the institution from the group path, so a collection-group read would be
 * denied — correctly, since it would otherwise span tenants. An institution has
 * a handful of groups, so a handful of listeners is the right shape.
 */
export function useDeviceCounts(): Map<string, number> {
  const { user } = useAuth();
  const institutionId = user?.institutionId ?? undefined;
  const { groups } = useGroups();
  const [perGroup, setPerGroup] = useState<Map<string, Map<string, number>>>(new Map());

  const groupIds = groups.map((group) => group.id).join(",");

  useEffect(() => {
    if (!institutionId || groupIds.length === 0) {
      setPerGroup(new Map());
      return;
    }

    const unsubscribes = groupIds.split(",").map((groupId) =>
      onSnapshot(
        collection(db(), "institutions", institutionId, "groups", groupId, "enrolments"),
        (snapshot) => {
          const counts = new Map<string, number>();
          for (const document of snapshot.docs) {
            // A revoked device is not a device somebody can join with, and
            // counting it would make "revoke" look as though it did nothing.
            if (document.get("revokedAt")) continue;
            const uid = document.get("uid") as string;
            counts.set(uid, (counts.get(uid) ?? 0) + 1);
          }
          setPerGroup((current) => new Map(current).set(groupId, counts));
        },
        () => {
          setPerGroup((current) => new Map(current).set(groupId, new Map()));
        }
      )
    );

    return () => unsubscribes.forEach((stop) => stop());
  }, [institutionId, groupIds]);

  return useMemo(() => {
    const total = new Map<string, number>();
    for (const counts of perGroup.values()) {
      for (const [uid, count] of counts) total.set(uid, (total.get(uid) ?? 0) + count);
    }
    return total;
  }, [perGroup]);
}

/**
 * One scenario, live.
 *
 * Read straight from Firestore. Scenarios live under the institution, so the
 * rules already scope them to it, and a subscription means a colleague's save
 * shows up here rather than being discovered by a failed write. There is no
 * callable for a single draft on purpose: a published *version* is fetched by
 * number, and the draft is simply the document.
 */
export function useScenario(scenarioId: string): {
  scenario: Scenario | undefined;
  loading: boolean;
  missing: boolean;
} {
  const { user } = useAuth();
  const institutionId = user?.institutionId ?? undefined;
  const [scenario, setScenario] = useState<Scenario | undefined>(undefined);
  const [loading, setLoading] = useState(true);
  const [missing, setMissing] = useState(false);

  useEffect(() => {
    if (!institutionId || !scenarioId) {
      setLoading(false);
      return;
    }

    setLoading(true);
    return onSnapshot(
      doc(db(), "institutions", institutionId, "scenarios", scenarioId),
      (snapshot) => {
        setScenario(snapshot.exists() ? (snapshot.data() as Scenario) : undefined);
        setMissing(!snapshot.exists());
        setLoading(false);
      },
      () => {
        setScenario(undefined);
        setMissing(true);
        setLoading(false);
      }
    );
  }, [institutionId, scenarioId]);

  return { scenario, loading, missing };
}

/** Every published version of a scenario, newest first. */
export function useScenarioVersions(scenarioId: string): {
  versions: Scenario[];
  loading: boolean;
} {
  const { user } = useAuth();
  const institutionId = user?.institutionId ?? undefined;
  const [versions, setVersions] = useState<Scenario[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!institutionId || !scenarioId) {
      setLoading(false);
      return;
    }

    setLoading(true);
    return onSnapshot(
      collection(db(), "institutions", institutionId, "scenarios", scenarioId, "versions"),
      (snapshot) => {
        setVersions(
          snapshot.docs
            .map((document) => document.data() as Scenario)
            .sort((a, b) => b.version - a.version)
        );
        setLoading(false);
      },
      () => {
        setVersions([]);
        setLoading(false);
      }
    );
  }, [institutionId, scenarioId]);

  return { versions, loading };
}

/**
 * A small institution collection, live, for the owner's checklist.
 *
 * Kits and devices are read straight from Firestore for the same reason the
 * groups are: the rules already say staff may read them, and a kit created in
 * another tab should tick the step here without anybody pressing anything.
 */
function useInstitutionCollection<T>(name: "kits" | "devices"): { items: T[]; loading: boolean } {
  const { user } = useAuth();
  const institutionId = user?.institutionId ?? undefined;
  const [items, setItems] = useState<T[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!institutionId) {
      setItems([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    return onSnapshot(
      collection(db(), "institutions", institutionId, name),
      (snapshot) => {
        setItems(snapshot.docs.map((document) => document.data() as T));
        setLoading(false);
      },
      () => {
        setItems([]);
        setLoading(false);
      }
    );
  }, [institutionId, name]);

  return { items, loading };
}

export function useKits(): { kits: Kit[]; loading: boolean } {
  const { items, loading } = useInstitutionCollection<Kit>("kits");
  return { kits: items, loading };
}

export function useDevices(): { devices: DeviceEnrolment[]; loading: boolean } {
  const { items, loading } = useInstitutionCollection<DeviceEnrolment>("devices");
  return { devices: items, loading };
}

/**
 * Whether a single scene exists, live.
 *
 * One document is enough to answer the checklist's question, so the query asks
 * for one: an institution with three hundred scenes should not download them
 * to find out that it has written its first.
 */
export function useHasScenes(): { hasScenes: boolean; loading: boolean } {
  const { user } = useAuth();
  const institutionId = user?.institutionId ?? undefined;
  const [hasScenes, setHasScenes] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!institutionId) {
      setHasScenes(false);
      setLoading(false);
      return;
    }

    setLoading(true);
    return onSnapshot(
      query(collection(db(), "institutions", institutionId, "scenarios"), limit(1)),
      (snapshot) => {
        setHasScenes(!snapshot.empty);
        setLoading(false);
      },
      () => {
        setHasScenes(false);
        setLoading(false);
      }
    );
  }, [institutionId]);

  return { hasScenes, loading };
}
